/**
 * HopeVoiceService - "Hope Reads Aloud" accessibility feature
 *
 * Uses ElevenLabs TTS API for natural-sounding speech via Hope's voice.
 * Falls back to Web Speech API if ElevenLabs is unavailable.
 *
 * Features:
 * - Read focused elements aloud on hover/focus
 * - Read page sections on demand
 * - Announce notifications and alerts
 * - Adjustable speed and volume
 * - English + Spanish support via eleven_multilingual_v2
 * - Auto-selects best available female voice (Web Speech fallback)
 */

export interface HopeVoiceSettings {
  enabled: boolean;
  rate: number;      // 0.5 - 2.0 (default 0.95 — natural pace)
  pitch: number;     // 0.5 - 2.0 (default 1.1 — slightly warm) [Web Speech only]
  volume: number;    // 0 - 1 (default 0.9)
  readOnFocus: boolean;    // Read elements when focused via keyboard
  readOnHover: boolean;    // Read elements when hovered (opt-in)
  announceAlerts: boolean; // Auto-read aria-live regions
  voiceName?: string;      // Preferred voice name (Web Speech fallback)
  language: 'en' | 'es';   // Language for ElevenLabs TTS
  useElevenLabs: boolean;  // Use ElevenLabs (true) or Web Speech (false)
}

const DEFAULT_SETTINGS: HopeVoiceSettings = {
  enabled: false,
  rate: 0.95,
  pitch: 1.1,
  volume: 0.9,
  readOnFocus: true,
  readOnHover: false,
  announceAlerts: true,
  language: 'en',
  useElevenLabs: true,
};

// Preferred female voice names for Web Speech fallback (ranked by quality)
const PREFERRED_VOICES = [
  'Microsoft Zira',        // Windows — warm, clear
  'Google US English',     // Chrome — natural
  'Samantha',              // macOS — friendly
  'Karen',                 // macOS Australian — warm
  'Moira',                 // macOS Irish — gentle
  'Victoria',              // macOS — clear
  'Microsoft Jenny',       // Windows 11
  'Google UK English Female',
  'Female',                // Generic fallback
];

class HopeVoiceServiceClass {
  private synth: SpeechSynthesis | null = null;
  private settings: HopeVoiceSettings = { ...DEFAULT_SETTINGS };
  private selectedVoice: SpeechSynthesisVoice | null = null;
  private voicesLoaded = false;
  private focusHandler: ((e: FocusEvent) => void) | null = null;
  private hoverHandler: ((e: MouseEvent) => void) | null = null;
  private mutationObserver: MutationObserver | null = null;
  private currentUtterance: SpeechSynthesisUtterance | null = null;
  private currentAudio: HTMLAudioElement | null = null;
  private lastReadText = '';
  private lastReadTime = 0;
  private speakQueue: string[] = [];
  private isSpeakingNow = false;
  private hasAnnouncedThisSession = false;

  constructor() {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      this.synth = window.speechSynthesis;
      this.loadVoices();
      // Voices load async in some browsers
      this.synth.addEventListener('voiceschanged', () => this.loadVoices());
    }
    this.loadSettings();
  }

  private loadVoices() {
    if (!this.synth) return;
    const voices = this.synth.getVoices();
    if (voices.length === 0) return;

    this.voicesLoaded = true;

    // If user has a saved preference, try that first
    if (this.settings.voiceName) {
      const saved = voices.find(v => v.name === this.settings.voiceName);
      if (saved) {
        this.selectedVoice = saved;
        return;
      }
    }

    // Auto-select best female voice
    for (const preferred of PREFERRED_VOICES) {
      const match = voices.find(v =>
        v.name.toLowerCase().includes(preferred.toLowerCase())
      );
      if (match) {
        this.selectedVoice = match;
        return;
      }
    }

    // Fallback: find any English female voice
    const englishFemale = voices.find(v =>
      v.lang.startsWith('en') &&
      (v.name.toLowerCase().includes('female') ||
       v.name.toLowerCase().includes('woman') ||
       v.name.toLowerCase().includes('zira') ||
       v.name.toLowerCase().includes('samantha'))
    );
    if (englishFemale) {
      this.selectedVoice = englishFemale;
      return;
    }

    // Last resort: any English voice
    this.selectedVoice = voices.find(v => v.lang.startsWith('en')) || voices[0];
  }

  private loadSettings() {
    try {
      const saved = localStorage.getItem('hopeVoiceSettings');
      if (saved) {
        this.settings = { ...DEFAULT_SETTINGS, ...JSON.parse(saved) };
      }
    } catch {
      // Use defaults
    }
  }

  saveSettings(newSettings: Partial<HopeVoiceSettings>) {
    this.settings = { ...this.settings, ...newSettings };
    localStorage.setItem('hopeVoiceSettings', JSON.stringify(this.settings));

    if (newSettings.voiceName) {
      this.loadVoices();
    }

    // Re-apply listeners based on new settings
    if (this.settings.enabled) {
      this.enable();
    } else {
      this.disable();
    }
  }

  getSettings(): HopeVoiceSettings {
    return { ...this.settings };
  }

  getAvailableVoices(): SpeechSynthesisVoice[] {
    if (!this.synth) return [];
    return this.synth.getVoices().filter(v => v.lang.startsWith('en'));
  }

  getSelectedVoiceName(): string {
    if (this.settings.useElevenLabs) return 'Hope (ElevenLabs)';
    return this.selectedVoice?.name || 'Default';
  }

  /**
   * Check if we're on a page where Hope should be active
   * Only active on authenticated client pages (dashboard, not login/register)
   */
  private isOnSafePage(): boolean {
    if (typeof window === 'undefined') return false;
    const path = window.location.pathname.toLowerCase();
    const unsafePages = ['/client-login', '/client-register', '/provider-login', '/provider-signup', '/set-password', '/login', '/register', '/'];
    // Block on login/register pages and landing page
    if (unsafePages.includes(path)) return false;
    return true;
  }

  /**
   * Speak text aloud as Hope — uses ElevenLabs or Web Speech fallback
   */
  speak(text: string, priority: 'polite' | 'assertive' = 'polite') {
    if (!this.settings.enabled) return;
    if (!text || text.trim().length === 0) return;
    // Don't speak on login/registration pages
    if (!this.isOnSafePage()) return;

    // Debounce: skip if same text within 500ms
    const now = Date.now();
    if (text === this.lastReadText && now - this.lastReadTime < 500) return;
    this.lastReadText = text;
    this.lastReadTime = now;

    // For assertive, cancel current speech
    if (priority === 'assertive') {
      this.stop();
    }

    if (this.settings.useElevenLabs) {
      this.speakElevenLabs(text);
    } else {
      this.speakWebSpeech(text);
    }
  }

  /**
   * Speak using ElevenLabs TTS API
   */
  private async speakElevenLabs(text: string) {
    // Queue if already speaking
    if (this.isSpeakingNow) {
      this.speakQueue.push(text);
      return;
    }

    this.isSpeakingNow = true;

    try {
      const response = await fetch('/api/elevenlabs-tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: text,
          language: this.settings.language,
        }),
      });

      if (!response.ok) {
        console.warn('[HopeVoice] ElevenLabs API error, falling back to Web Speech');
        this.isSpeakingNow = false;
        this.speakWebSpeech(text);
        return;
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audio.volume = this.settings.volume;
      // Adjust playback rate (ElevenLabs already sounds natural at 1x)
      audio.playbackRate = this.settings.rate > 1.5 ? 1.5 : this.settings.rate < 0.7 ? 0.7 : this.settings.rate;

      this.currentAudio = audio;

      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = null;
        this.isSpeakingNow = false;
        // Process next in queue
        if (this.speakQueue.length > 0) {
          const next = this.speakQueue.shift()!;
          this.speakElevenLabs(next);
        }
      };

      audio.onerror = () => {
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = null;
        this.isSpeakingNow = false;
        console.warn('[HopeVoice] Audio playback error');
      };

      await audio.play();
    } catch (error) {
      console.warn('[HopeVoice] ElevenLabs failed, falling back to Web Speech:', error);
      this.isSpeakingNow = false;
      this.speakWebSpeech(text);
    }
  }

  /**
   * Speak using Web Speech API (fallback)
   */
  private speakWebSpeech(text: string) {
    if (!this.synth) return;

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = this.settings.rate;
    utterance.pitch = this.settings.pitch;
    utterance.volume = this.settings.volume;

    if (this.selectedVoice) {
      utterance.voice = this.selectedVoice;
    }

    utterance.onend = () => {
      this.currentUtterance = null;
    };
    utterance.onerror = () => {
      this.currentUtterance = null;
    };

    this.currentUtterance = utterance;
    this.synth.speak(utterance);
  }

  /**
   * Stop current speech
   */
  stop() {
    // Stop ElevenLabs audio
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
    this.isSpeakingNow = false;
    this.speakQueue = [];

    // Stop Web Speech
    if (this.synth) {
      this.synth.cancel();
      this.currentUtterance = null;
    }
  }

  /**
   * Check if currently speaking
   */
  isSpeaking(): boolean {
    if (this.currentAudio && !this.currentAudio.paused) return true;
    return this.synth?.speaking || false;
  }

  /**
   * Read a specific element's accessible text
   */
  readElement(element: HTMLElement) {
    const text = this.getAccessibleText(element);
    if (text) {
      this.speak(text);
    }
  }

  /**
   * Read all content in a container (for "Read Page" feature)
   */
  readPageSection(container: HTMLElement) {
    const text = this.getPageText(container);
    if (text) {
      this.speak(text);
    }
  }

  /**
   * Get accessible text from an element
   */
  private getAccessibleText(el: HTMLElement): string {
    // Priority: aria-label > aria-labelledby > alt > title > visible text
    const ariaLabel = el.getAttribute('aria-label');
    if (ariaLabel) return ariaLabel;

    const labelledBy = el.getAttribute('aria-labelledby');
    if (labelledBy) {
      const labelEl = document.getElementById(labelledBy);
      if (labelEl) return labelEl.textContent?.trim() || '';
    }

    // For images
    if (el instanceof HTMLImageElement) {
      return el.alt || el.title || 'Image';
    }

    // For inputs — NEVER read password or email values (security)
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
      const label = document.querySelector(`label[for="${el.id}"]`);
      const labelText = label?.textContent?.trim() || '';
      // Never read sensitive field values
      const sensitiveTypes = ['password', 'email', 'tel', 'ssn'];
      const inputType = (el as HTMLInputElement).type?.toLowerCase() || '';
      if (sensitiveTypes.includes(inputType)) {
        return labelText || `${inputType} field`;
      }
      const role = el.type === 'checkbox' ? (el as HTMLInputElement).checked ? 'checked' : 'unchecked' : '';
      const value = el instanceof HTMLSelectElement
        ? el.options[el.selectedIndex]?.text || ''
        : el.value || '';
      return `${labelText}${role ? `, ${role}` : ''}${value ? `, ${value}` : ''}`.trim();
    }

    // For buttons
    if (el instanceof HTMLButtonElement || el.getAttribute('role') === 'button') {
      return el.textContent?.trim() || el.getAttribute('title') || 'Button';
    }

    // For links
    if (el instanceof HTMLAnchorElement) {
      return el.textContent?.trim() || el.getAttribute('title') || 'Link';
    }

    return el.textContent?.trim() || '';
  }

  /**
   * Get readable text from a page section.
   * Groups text by block-level elements so headings + subtext read naturally.
   * e.g. "Provider Info: View provider profile" instead of "Provider. Info. View. provider. profile"
   */
  private getPageText(container: HTMLElement): string {
    const BLOCK_TAGS = new Set([
      'DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
      'LI', 'TR', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER',
      'NAV', 'ASIDE', 'MAIN', 'BLOCKQUOTE', 'FIGCAPTION',
    ]);
    const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'SVG', 'CANVAS']);
    const HEADING_TAGS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6']);

    const parts: string[] = [];
    const seen = new Set<string>();

    const collectText = (el: Element) => {
      // Skip hidden elements
      if (el instanceof HTMLElement) {
        if (el.hidden || el.getAttribute('aria-hidden') === 'true') return;
        if (SKIP_TAGS.has(el.tagName)) return;
        // Skip elements with no visible height (likely hidden)
        if (el.offsetHeight === 0 && el.offsetWidth === 0) return;
      }

      // Summarize <nav> sections — read section headers + item count, not every item
      if (el.tagName === 'NAV') {
        const navLabel = el.getAttribute('aria-label') || 'Navigation';
        const groups = el.querySelectorAll('[role="group"]');
        if (groups.length > 0) {
          const groupSummaries: string[] = [];
          groups.forEach(group => {
            const headingId = group.getAttribute('aria-labelledby');
            const heading = headingId ? document.getElementById(headingId) : null;
            const headingText = heading?.textContent?.trim();
            const items = group.querySelectorAll('button, a');
            if (headingText) {
              groupSummaries.push(`${headingText}: ${items.length} items`);
            }
          });
          if (groupSummaries.length > 0) {
            const summary = `${navLabel}: ${groupSummaries.join('. ')}`;
            if (!seen.has(summary)) {
              seen.add(summary);
              parts.push(summary);
            }
          }
        } else {
          const items = el.querySelectorAll('button, a');
          const summary = `${navLabel}: ${items.length} items`;
          if (!seen.has(summary)) {
            seen.add(summary);
            parts.push(summary);
          }
        }
        return; // Don't recurse into nav children
      }

      // For block elements, get their full text content as one phrase
      if (BLOCK_TAGS.has(el.tagName)) {
        const text = el.textContent?.trim();
        if (text && text.length > 0 && text.length < 500 && !seen.has(text)) {
          // Check if any child is also a block element
          const hasBlockChild = Array.from(el.children).some(
            child => BLOCK_TAGS.has(child.tagName)
          );

          if (!hasBlockChild) {
            // Leaf block — read its full text as one unit
            seen.add(text);
            // If it's a heading, add a colon for natural reading
            if (HEADING_TAGS.has(el.tagName)) {
              parts.push(text + ':');
            } else {
              parts.push(text);
            }
            return; // Don't recurse into children — we got the full text
          }
        }
      }

      // Recurse into children for container elements
      for (const child of Array.from(el.children)) {
        collectText(child);
      }
    };

    collectText(container);

    // Join with periods between blocks, clean up double punctuation
    return parts
      .join('. ')
      .replace(/:\./g, ':')
      .replace(/\.\s*\./g, '.')
      .replace(/\s+/g, ' ')
      .substring(0, 5000);
  }

  /**
   * Enable Hope voice — attach listeners
   */
  enable() {
    this.settings.enabled = true;
    this.saveSettingsQuiet();

    // Focus listener: read element when keyboard-focused
    if (this.settings.readOnFocus && !this.focusHandler) {
      this.focusHandler = (e: FocusEvent) => {
        const target = e.target as HTMLElement;
        if (target && target !== document.body) {
          this.readElement(target);
        }
      };
      document.addEventListener('focusin', this.focusHandler);
    }

    // Hover listener (opt-in): read element on hover
    if (this.settings.readOnHover && !this.hoverHandler) {
      this.hoverHandler = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target && ['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName) ||
            target?.getAttribute('role') === 'button' ||
            target?.getAttribute('tabindex')) {
          this.readElement(target);
        }
      };
      document.addEventListener('mouseover', this.hoverHandler);
    }

    // Mutation observer: announce aria-live region changes
    if (this.settings.announceAlerts && !this.mutationObserver) {
      this.mutationObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList' || mutation.type === 'characterData') {
            const target = mutation.target as HTMLElement;
            const liveRegion = target.closest?.('[aria-live]') as HTMLElement;
            if (liveRegion) {
              const priority = liveRegion.getAttribute('aria-live') === 'assertive' ? 'assertive' : 'polite';
              const text = liveRegion.textContent?.trim();
              if (text) {
                this.speak(text, priority);
              }
            }
          }
        }
      });
      this.mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    // Announce activation — only once per session
    if (!this.hasAnnouncedThisSession) {
      this.hasAnnouncedThisSession = true;
      this.speak("Hope is here. I'll read the screen for you.", 'assertive');
    }
  }

  /**
   * Disable Hope voice — remove listeners
   */
  disable() {
    this.stop();
    this.settings.enabled = false;
    this.saveSettingsQuiet();

    if (this.focusHandler) {
      document.removeEventListener('focusin', this.focusHandler);
      this.focusHandler = null;
    }
    if (this.hoverHandler) {
      document.removeEventListener('mouseover', this.hoverHandler);
      this.hoverHandler = null;
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }

  /**
   * Toggle on/off
   */
  toggle() {
    if (this.settings.enabled) {
      this.disable();
    } else {
      this.enable();
    }
    return this.settings.enabled;
  }

  /**
   * Set language (en/es) for ElevenLabs TTS
   */
  setLanguage(lang: 'en' | 'es') {
    this.settings.language = lang;
    this.saveSettingsQuiet();
  }

  /**
   * Get current language
   */
  getLanguage(): 'en' | 'es' {
    return this.settings.language;
  }

  private saveSettingsQuiet() {
    localStorage.setItem('hopeVoiceSettings', JSON.stringify(this.settings));
  }
}

// Singleton
export const HopeVoiceService = new HopeVoiceServiceClass();
